import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import { auth, db } from "../firebase/firebaseConfig";
import { doc, getDoc, updateDoc, collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

function Profile() {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [newName, setNewName] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [finishedMatchIds, setFinishedMatchIds] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      const matchSnap = await getDocs(collection(db, "matches"));
      const ids = new Set();
      matchSnap.forEach(d => {
        if (d.data().status === 'finished') ids.add(d.data().id);
      });
      setFinishedMatchIds(ids);

      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData(data);
          setNewName(data.displayName || "");
          setProfileImage(data.profileImage || "");
        }
      } else {
        navigate("/");
      }
      setLoading(false);
    };

    const unsub = auth.onAuthStateChanged((u) => {
      if (u) fetchUserData();
      else navigate("/");
    });
    return () => unsub();
  }, [navigate]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1048487) {
        alert("Image too large. Please choose an image smaller than 1MB.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => setProfileImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateProfile = async () => {
    if (!newName.trim()) return;
    setIsUpdating(true);
    try {
      const user = auth.currentUser;
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        displayName: newName,
        profileImage: profileImage
      });
      alert("Arena Credentials Updated! 🏆");
    } catch (err) {
      console.error(err);
      alert("Update failed.");
    } finally {
      setIsUpdating(false);
    }
  };

  const calculateAccuracy = (correct, userPredictions) => {
    if (!userPredictions || userPredictions.length === 0) return 0;
    const evaluatedCount = userPredictions.filter(p => finishedMatchIds.has(p.matchId)).length;
    if (evaluatedCount === 0) return 0;
    return Math.round((correct / evaluatedCount) * 100);
  };

  if (loading) return <div style={styles.loader}>Accessing Command Center...</div>;

  return (
    <div style={{ minHeight: "100vh", background: 'var(--stadium-gradient)' }}>
      <Navbar />
      <div className="app-container" style={styles.container}>
        <div className="glass-morphism animate-fade-in" style={styles.profileCard}>
          <div style={styles.grid}>
            {/* 📸 Image Section */}
            <div style={styles.imageSection}>
              <div style={styles.imagePlaceholder}>
                {profileImage ? (
                  <img src={profileImage} alt="Profile" style={styles.avatar} />
                ) : (
                  <span style={{ fontSize: "4rem", opacity: 0.2 }}>👤</span>
                )}
                <label style={styles.uploadBtn}>
                  📸
                  <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: "none" }} />
                </label>
              </div>
              <div style={styles.pointsBadge}>
                <span style={styles.pointsSub}>TOTAL SCORE</span>
                <span style={styles.pointsVal}>{userData?.points || 0}</span>
              </div>
            </div>

            {/* 📝 Info Section */}
            <div style={styles.infoSection}>
              <h1 className="gradient-text" style={styles.headerTitle}>PLAYER PROFILE</h1>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginTop: '-1rem' }}>Manage your official Arena identity.</p>

              <div style={styles.inputGroup}>
                <label style={styles.label}>ARENA NAME</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Enter Display Name"
                  style={styles.input}
                />
              </div>

              <div style={styles.statsGrid}>
                <div style={styles.statBox}>
                  <span style={styles.statVal}>{userData?.totalPredictions || 0}</span>
                  <span style={styles.statLab}>PREDICTIONS</span>
                </div>
                <div style={styles.statBox}>
                  <span style={styles.statVal}>{calculateAccuracy(userData?.correctPredictions, userData?.predictions)}%</span>
                  <span style={styles.statLab}>EFFICIENCY</span>
                </div>
              </div>

              <button
                onClick={handleUpdateProfile}
                className="clickable"
                style={{ ...styles.saveBtn, opacity: isUpdating ? 0.6 : 1 }}
                disabled={isUpdating}
              >
                {isUpdating ? "SYNCING..." : "UPDATE PROFILE"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { padding: "2rem 1rem", display: 'flex', justifyContent: 'center' },
  profileCard: { width: "100%", maxWidth: "900px", padding: "3rem 2rem", borderRadius: "32px" },
  grid: { display: "flex", gap: "3rem", flexWrap: "wrap", justifyContent: "center" },
  imageSection: { display: "flex", flexDirection: "column", alignItems: "center", gap: "2rem", minWidth: '220px' },
  imagePlaceholder: { width: "200px", height: "200px", borderRadius: "50%", background: "var(--bg-secondary)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", border: '4px solid var(--primary)', overflow: 'hidden', boxShadow: 'var(--card-shadow)' },
  avatar: { width: "100%", height: "100%", objectFit: "cover" },
  uploadBtn: { position: "absolute", bottom: "10px", right: "10px", background: "var(--primary)", width: "40px", height: "40px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "1.2rem", border: '2px solid white', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' },
  infoSection: { flex: 1, display: "flex", flexDirection: "column", gap: "2rem", minWidth: "300px" },
  headerTitle: { fontSize: "2.5rem" },
  inputGroup: { display: "flex", flexDirection: "column", gap: "0.6rem" },
  label: { fontSize: "0.7rem", color: "var(--text-secondary)", fontWeight: "800", letterSpacing: "0.15em" },
  input: { background: "var(--bg-primary)", border: "1px solid var(--glass-border)", color: "var(--text-primary)", padding: "1.2rem", borderRadius: "16px", fontSize: "1.1rem", fontWeight: '700', outline: "none", transition: '0.3s' },
  pointsBadge: { padding: '1rem 2rem', background: 'var(--bg-secondary)', borderRadius: '20px', border: '1px solid var(--glass-border)', textAlign: 'center', minWidth: '180px' },
  pointsSub: { display: 'block', fontSize: '0.6rem', fontWeight: '800', opacity: 0.6, letterSpacing: '0.1em' },
  pointsVal: { fontSize: '2rem', fontWeight: '900', color: 'var(--primary)' },
  statsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" },
  statBox: { padding: "1.5rem", borderRadius: "20px", background: "rgba(255,255,255,0.02)", display: "flex", flexDirection: "column", alignItems: "center", border: '1px solid var(--glass-border)' },
  statVal: { fontSize: "1.8rem", fontWeight: "900", color: 'var(--text-primary)' },
  statLab: { fontSize: "0.6rem", color: "var(--text-secondary)", fontWeight: "900", letterSpacing: '0.05em' },
  saveBtn: { padding: "1.2rem", borderRadius: "16px", background: "var(--primary)", color: "white", fontWeight: "900", fontSize: "1rem", boxShadow: "0 10px 25px rgba(0, 118, 255, 0.3)" },
  loader: { textAlign: "center", padding: "12rem", color: "var(--primary)", fontSize: "1.5rem", fontWeight: "900" }
};

export default Profile;
